/**
 * ScanResultsPage — Issue #130
 *
 * Design system: dark hero / editorial body.
 * IA: impact-first — aggregate all issues across sections, sort by severity
 * weight → lead with actionable gaps, then section breakdowns, then wins.
 * Dynamic prominence: sections ordered by compliance %, worst first.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScanResult, EXPECTED_SCAN_RESULT_SCHEMA_VERSION } from '../utils/types';
import { formatDateTime } from '../utils/date';
import ScanDisclaimer from '../components/ScanDisclaimer';
import PartialScanBanner from '../components/PartialScanBanner';
import LockedReportView from '../components/LockedReportView';
import PaywallOffer, { paywallStateFor, SignedOutUnlock } from '../components/PaywallOffer';
import ShareModal from '../components/ShareModal';

// Report Components
import { T } from '../components/report/tokens';
import ReportHero from '../components/report/ReportHero';
import { isGpcOnlyScan } from '../components/report/gpc-verdict';
import FixList from '../components/report/FixList';
import CipaExposurePanel from '../components/report/CipaExposurePanel';
import ShieldOffer from '../components/report/ShieldOffer';
import PassingChecks from '../components/report/PassingChecks';
import ProofAppendix, { ProofAppendixHandle } from '../components/report/ProofAppendix';
import ScanErrorsPanel from '../components/report/ScanErrorsPanel';
import CrosscheckBanner from '../components/report/CrosscheckBanner';
import { CrosscheckExplainerProvider } from '../components/report/crosscheck/ExplainerContext';
import { collectCcpaChecks, countCrosscheck } from '../components/report/crosscheck';
import BlockedBySiteView from '../components/report/BlockedBySiteView';
import { audienceFor, consumerVerdict } from '../utils/audience';

interface ScanResultsPageProps {
  result: ScanResult;
  onBack: () => void;
  reportId?: string | null;
  userId?: string | null;
  isSignedIn?: boolean;
  authEnabled?: boolean;
  balance?: number | null;
  creditsLoading?: boolean;
  /**
   * Pack pricing from useCreditInfo, threaded down to LockedReportView.
   * Optional only because the values are genuinely absent until
   * GET /api/credits answers (#970) — never optional-with-a-default: these
   * are env-configurable (CREDIT_PACK_PRICE_CENTS, CREDITS_PER_PURCHASE), so
   * a default anywhere on the path would advertise stale numbers while
   * Stripe charges the real ones.
   */
  packPrice?: number;
  creditsPerPurchase?: number;
  /**
   * False for an account that only ever bought "unlock this report" (#1776):
   * it never bought credits, so the delivered report says nothing about them.
   */
  hasCreditHistory?: boolean;
  onRunFullAudit?: (url: string) => Promise<void> | void;
  /** Reload the result after a locked full audit was unlocked in place (#1762). */
  onUnlocked?: () => void;
  signInButton?: React.ReactNode;
  /** #1779: the signed-out claim holder's unlock path, threaded to PaywallOffer. */
  signedOutUnlock?: SignedOutUnlock;
}

export default function ScanResultsPage({
  result,
  onBack,
  reportId,
  userId,
  isSignedIn,
  authEnabled,
  balance,
  creditsLoading,
  packPrice,
  creditsPerPurchase,
  hasCreditHistory,
  onRunFullAudit,
  onUnlocked,
  signInButton,
  signedOutUnlock,
}: ScanResultsPageProps) {
  const appendixRef = useRef<ProofAppendixHandle>(null);
  const handleOpenEvidence = useCallback((ref: { section: string; filter?: string }) => {
    appendixRef.current?.openTo(ref.section, ref.filter);
  }, []);

  const [showShareModal, setShowShareModal] = useState(false);
  const canShare = Boolean(reportId) && Boolean(userId) && result.licenseStatus !== 'locked';

  useEffect(() => {
    if (result.schemaVersion !== EXPECTED_SCAN_RESULT_SCHEMA_VERSION) {
      console.warn(
        `Schema version mismatch: expected ${EXPECTED_SCAN_RESULT_SCHEMA_VERSION}, got ${result.schemaVersion}`
      );
    }
  }, [result.schemaVersion]);

  // The hero's check set (#1899): FixList and PassingChecks must count what
  // the Issues / Warnings / Passing chips above them count.
  const allChecks = useMemo(() => collectCcpaChecks(result), [result]);
  const crosscheckCounts = useMemo(() => countCrosscheck(allChecks), [allChecks]);
  // #2046: a scan started on /check is framed for a visitor who doesn't run
  // the site. Read from this tab's storage on every render (not memoized):
  // the claim hook stores the flag from the URL, and a reload or the unlock
  // round trip keeps it for this scan id only.
  const consumer = result.licenseStatus === 'locked' && audienceFor(result.id) === 'consumer'
    ? consumerVerdict(result)
    : undefined;
  // Privisy Shield fake door: for the site's owner side of a finished report,
  // never a /check visitor, who doesn't run the site.
  const shieldOffer = reportId && result.status === 'completed' && !consumer
    ? <ShieldOffer reportId={reportId} checks={allChecks} />
    : null;

  /**
   * Issue #953 — the credit model, disclosed while the user is holding the
   * report rather than for the first time by the 402 that refuses their
   * second scan.
   *
   * Pack pricing and balance arrive in the same GET /api/credits response, so
   * pricing being present is the signal that the account was actually read
   * (#970). Without it `balance` is `null`, which is indistinguishable from a
   * genuine unlimited account, and `paywallStateFor` returns 'none' — nothing
   * renders, which is the only honest outcome for an account we never read.
   */
  const creditsResolved = packPrice !== undefined && creditsPerPurchase !== undefined;
  const creditModelState = paywallStateFor({ authEnabled, isSignedIn, balance, creditsResolved });
  /**
   * Only these two states have a credit model to disclose (see PaywallOffer's
   * 'report-delivered' branch), and only a finished report is delivered
   * value: a failed scan has handed the user nothing, so introducing the
   * economics there would land as the bill for a non-delivery. Gating here as
   * well as inside PaywallOffer keeps the 40px spacer from rendering around
   * an empty card.
   */
  const showCreditModel =
    result.status === 'completed' &&
    hasCreditHistory !== false &&
    (creditModelState === 'has-credits' || creditModelState === 'out-of-credits');

  // ── Blocked-by-site early return ──────────────────────────────────────
  if (result.status === 'failed' && result.blockedBySite) {
    return <BlockedBySiteView result={result} onBack={onBack} />;
  }





  // Below the report, not between the hero and the first finding (#1655):
  // the fix list has to start above the fold.
  const feedbackBanner = (
    <div className="feedback-banner feedback-banner--footer">
      <span className="feedback-banner__text">
        Questions or spotted something off? We&apos;d love your feedback.
      </span>
      <a
        href={`${process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com'}/contact`}
        target="_blank"
        rel="noopener noreferrer"
        className="feedback-banner__link"
      >
        Send feedback →
      </a>
    </div>
  );

  return (
    <>
    <div className="scan-body" style={{ minHeight: '100dvh', padding: 0 }}>
      {/* Light document header (#1937): toolbar, then the header on the
          same 880px column as the body. */}
      <div className="scan-hero">
        <div className="scan-hero__nav">
          <button onClick={onBack} className="scan-hero__back-btn">
            ← Back
          </button>
          <span className="scan-hero__url">{result.url}</span>
          {result.status === 'completed' && (
            <span className="scan-hero__saved-badge">
              <span className="scan-hero__saved-dot" />
              Saved as report
            </span>
          )}
          <div className="scan-hero__nav-right">
            {canShare && (
              <div className="scan-hero__actions">
                <button
                  onClick={() => setShowShareModal(true)}
                  className="scan-hero__action-btn"
                >
                  Share
                </button>
                <button
                  onClick={() => window.print()}
                  className="scan-hero__action-btn"
                >
                  Print
                </button>
              </div>
            )}
            <span className="scan-hero__timestamp">
              {formatDateTime(result.startedAt)}
            </span>
          </div>
        </div>


        <div className="scan-hero__body">
          <div className="report-column">
            <div style={{ marginBottom: '16px' }}>
              <ScanDisclaimer dismissible />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <PartialScanBanner result={result} />
            </div>

            {(result.schemaVersion === undefined || result.schemaVersion < 2) && (
              <div style={{ marginBottom: '16px' }}>
                <div className="schema-alert" role="alert" id="schema-version-banner">
                  <div className="schema-alert__text">
                    Notice: This report was scanned with an older system version. Some detail grids may have limited interactivity.
                  </div>
                </div>
              </div>
            )}

            {/* A locked report's grade and section results live in the shared
                teaser below (#1807), so the header would say them twice. */}
            {result.licenseStatus !== 'locked' && <ReportHero result={result} />}
          </div>
        </div>
      </div>

      {/* BODY — light */}
      <div className="scan-body">
        {/* DESIGN.md's 880px document column (#1655), header included (#1937). */}
        <div className="report-column" data-testid="report-column">
          {result.licenseStatus === 'locked' ? (
            <>
              <LockedReportView
                reportId={reportId}
                scanId={result.id}
                userId={userId}
                isSignedIn={isSignedIn}
                authEnabled={authEnabled}
                balance={balance}
                creditsLoading={creditsLoading}
                packPrice={packPrice}
                creditsPerPurchase={creditsPerPurchase}
                onRunFullAudit={onRunFullAudit}
                onUnlocked={onUnlocked}
                signInButton={signInButton}
                signedOutUnlock={signedOutUnlock}
                gpcOnly={isGpcOnlyScan(result)}
                onBack={onBack}
                consumer={consumer}
                alternateOffer={shieldOffer}
                checks={consumer ? undefined : allChecks}
              />
              {feedbackBanner}
            </>
          ) : (
            <>
              <div style={{ backgroundColor: 'transparent', marginBottom: 40 }}>
                {/* Introduces AI Crosscheck once, above section 01 (#1658), over
                    the hero's CCPA check set. */}
                <CrosscheckExplainerProvider counts={crosscheckCounts}>
                  <CrosscheckBanner counts={crosscheckCounts} status={result.crosscheckStatus} />
                  <ScanErrorsPanel errors={result.errors || []} />
                  <FixList checks={allChecks} onOpenEvidence={handleOpenEvidence} />
                  <PassingChecks checks={allChecks} />
                </CrosscheckExplainerProvider>
                {/* CIPA § 631 exposure (#1392): read on its own, never folded
                    into allChecks, which feeds the CCPA fix list and counts. */}
                <CipaExposurePanel checks={result.sections?.cipa?.checks ?? []} />
                {shieldOffer}
              </div>

              <div style={{ backgroundColor: 'transparent', borderTop: `1px solid ${T.lightBorder}`, paddingTop: 40 }}>
                <ProofAppendix ref={appendixRef} result={result} reportId={reportId} />
              </div>

              {feedbackBanner}

              {/* The report is delivered and read; this is the last point
                  before the user goes back for a second scan, and the first
                  point at which the credit model can be stated while they are
                  holding something rather than being refused something. The
                  post-claim screen (#952) deliberately stays silent about
                  credits so these two moments do not both speak. */}
              {showCreditModel && (
                <div style={{ marginTop: 40 }}>
                  <PaywallOffer
                    state={creditModelState}
                    context="report-delivered"
                    userId={userId}
                    balance={balance}
                    packPrice={packPrice}
                    creditsPerPurchase={creditsPerPurchase}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {showShareModal && reportId && (
      <ShareModal
        reportId={reportId}
        reportName={result.url}
        onClose={() => setShowShareModal(false)}
      />
    )}
    </>
  );
}
